"use client";

import AppHeader from "@/app/dashboard/AppHeader";
import { useTheme } from "@/app/providers/ThemeContext";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { apiRequest } from "@/shared/lib/apiClient";
import { queryKeys } from "@/shared/lib/queryKeys";
import { OptionsSnapshotResponse } from "@/shared/lib/types";
import { SkeletonChart, SkeletonStyles, SkeletonText } from "@/shared/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import {
    Activity,
    BarChart2,
    Calculator,
    ChevronDown,
    List,
    Sparkles,
    Zap,
} from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { fetchIdeaContext } from "@/domains/insights/lib/tickerIdeaContext";
import {
    breakevenMarkLines,
    exitWindowShading,
    exitWindowToEchartsMarkAreaData,
    priceLevelsToEchartsMarkLineData,
    strikeMarkLines,
} from "@/domains/insights/lib/tickerIdeaOverlays";
// JS-1097 (adversarial-review fix): the SAME entry-level four-question
// composition the screener drawer and the daily edition's expandable card
// both assemble their own body from (ThesisQuestionSections.tsx's module
// comment) -- a genuine shared composition (NO-TECH-DEBT), never a parallel
// hand-rolled reconstruction, so the ticker view's "full argument" states
// every fact (including provenance_tags) the other two surfaces do for the
// same entry.
import { EntryFourQuestionArgument } from "@/domains/trade-ideas/components/ThesisQuestionSections";
import {
    archetypeLabel,
    directionView,
    exitRuleCopy,
    projectedDisplayExpiry,
    projectedDisplayStrikes,
    reasonCodeLabel,
} from "@/domains/trade-ideas/lib/screenerLabels";
import { buildBackToIdeaHref, readTickerContextParams, reconcileBackToIdeaRowId } from "@/domains/trade-ideas/lib/tickerContextLink";
import { cn } from "@/shared/lib/utils";

// Lazy load heavy chart components
const OptionsChainPanel = lazy(() => import("@/domains/insights/components/OptionsChainPanel"));
const AIInsightPanel = lazy(() => import("@/domains/insights/components/AIInsightPanel"));

type MarketSentimentLabel = "Positive" | "Negative" | "Neutral";

interface MarketSentimentResponse {
    status: string;
    ticker: string;
    sentiment: MarketSentimentLabel;
    summary: string;
    sources?: Array<{
        title: string;
        url: string;
        source?: string;
    }>;
    as_of: string;
    model: string;
}

// Suspense fallback for lazy-loaded components
const TabFallback = () => (
    <div className="flex-1 flex items-center justify-center" style={{ background: "var(--v2-bg-surface)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "80%" }}>
            <SkeletonText width="100%" height={12} />
            <SkeletonText width="85%" height={12} />
            <SkeletonText width="70%" height={12} />
        </div>
    </div>
);

const INDEX_LIKE_SYMBOLS = new Set(["SPX", "SPXW", "NDX", "RUT", "DJI", "VIX"]);
const SYMBOL_API_MAP: Record<string, string> = {
    SPX: "SPY",
    SPXW: "SPY",
};

export default function ProTraderDashboardV2() {
    const { themeMode } = useTheme();

    // Resolve theme colors for ECharts (which doesn't natively support CSS variables in canvas)
    const isDark =
        themeMode === "dark" ||
        (themeMode === "system" &&
            typeof window !== "undefined" &&
            window.matchMedia("(prefers-color-scheme: dark)").matches);

    const chartColors = {
        axisLabel: isDark ? "#94a3b8" : "#64748b",
        gridLine: isDark ? "#2b3139" : "#e2e8f0",
        tooltipBg: isDark ? "#121519" : "#ffffff",
        tooltipText: isDark ? "#ffffff" : "#0f172a",
        tooltipBorder: isDark ? "#2b3139" : "#e2e8f0",
    };
    const params = useParams();
    const symbol = (params?.ticker as string)?.toUpperCase() || "SOL";
    const apiSymbol = SYMBOL_API_MAP[symbol] || symbol;

    // JS-1082: "viewing in context of a Desk idea" -- a SHAREABLE query-param
    // deep link (see tickerContextLink.ts's module docstring for why this is
    // a query param, not JS-1081's one-shot router-state convention).
    // Absent params (the overwhelmingly common case) mean `resolvedContext`
    // stays null and the page renders EXACTLY as it does today
    // (regression-pinned in TickerDashboard.test.tsx).
    const [searchParams] = useSearchParams();
    const tickerContextParams = useMemo(() => readTickerContextParams(searchParams), [searchParams]);
    const ideaContextQuery = useQuery({
        queryKey: queryKeys.tradeIdeaContext(tickerContextParams?.ledgerId ?? ""),
        queryFn: ({ signal }) => fetchIdeaContext(tickerContextParams!.ledgerId, { signal }),
        enabled: Boolean(tickerContextParams?.ledgerId),
        staleTime: 30_000,
        // fetchIdeaContext never rejects (it swallows every failure into a
        // tolerant `null`) -- retries would only delay the honest
        // context-free fallback for no benefit.
        retry: false,
    });
    // Tolerant zod decode already happened inside fetchIdeaContext -- a
    // malformed/absent/unresolvable ledger id surfaces here as `null`,
    // exactly like "no context param at all." The page has ONE fallback
    // path for every one of those cases: render context-free.
    const fetchedIdeaContext = tickerContextParams ? ideaContextQuery.data ?? null : null;

    // JS-1082 round 3 (FIX 1, fail-closed instrument binding): the response's
    // OWN ticker MUST be a non-empty string that EXACTLY equals this chart's
    // route symbol before ANY context renders. Round 2's check only REJECTED
    // when a truthy ticker disagreed -- a null/empty ticker (schema-permitted:
    // `ticker: z.string().nullish()` on `ideaContextResponseSchema`, reachable
    // via a partial/legacy ledger document) fell through as "no mismatch" and
    // was accepted for ANY chart. This is now a POSITIVE match requirement
    // (fail CLOSED on any falsy/absent ticker), not a negative mismatch check
    // (fail OPEN on absence) -- a crafted or stale link (e.g.
    // `/ticker/TSLA?ideaLedgerId=<an NVDA idea>`, or a ledger-tier response
    // with no `listing.ticker`) must never overlay an unrelated or
    // unverifiable instrument's strikes/breakeven/exit rule onto this chart.
    // Checked AFTER decode (never trusted from the URL alone) against the
    // resolved response's `ticker` field, which BOTH resolution tiers
    // populate when the underlying data actually carries one (TIER 1 from
    // the live row's own `ticker`; TIER 2 from the ledger document's own
    // `listing.ticker`) -- but neither tier is guaranteed to on a
    // partial/legacy document, hence the explicit non-empty check here.
    const resolvedTicker = fetchedIdeaContext?.ticker?.trim().toUpperCase() || "";
    const instrumentVerified = resolvedTicker.length > 0 && resolvedTicker === apiSymbol.toUpperCase();
    const resolvedIdeaContext = instrumentVerified ? fetchedIdeaContext : null;

    // JS-1082 round 2 (FIX 4, back-link trust): a URL-supplied `ideaRowId`
    // is UNTRUSTED input (a hand-edited or stale link) -- reconcile it
    // against the RESOLVED response's own `row_id` and drop it on any
    // mismatch, rather than building a back-link that would reopen the
    // WRONG row's drawer/card on the origin surface.
    //
    // JS-1094 (demo blocker FIX 2): this reconciliation is for a
    // "screener"/"edition" origin's `row_id` ONLY -- a "ledger" origin's
    // back-link is built from `resolvedIdeaContext.ledger_id` instead (see
    // `backToIdeaHref` below), never from this reconciled value. Reconciling
    // a ledger row's identity against the screener `row_id` field was the
    // exact bug: the two are different identity concepts, so the ledger's
    // real id was reconciled against the wrong field and dropped on the
    // near-certain mismatch, losing the `?ledgerTarget=` backlink.
    const trustedBackToIdeaRowId = resolvedIdeaContext
        ? reconcileBackToIdeaRowId(tickerContextParams?.rowId ?? null, resolvedIdeaContext.row_id ?? null)
        : null;
    const backToIdeaHref = resolvedIdeaContext && tickerContextParams
        ? buildBackToIdeaHref({
              origin: tickerContextParams.origin,
              rowId: trustedBackToIdeaRowId,
              editionAsOf: tickerContextParams.editionAsOf,
              ledgerId: resolvedIdeaContext.ledger_id,
          })
        : null;

    const [activeBottomTab, setActiveBottomTab] = useState("open-interest");
    const [optionType, setOptionType] = useState<"call" | "put" | "both">("both");
    const [selectedExpiry, setSelectedExpiry] = useState<string>("");
    const [activeGreek, setActiveGreek] = useState<
        "delta" | "gamma" | "theta" | "vega"
    >("delta");
    const [timeframe, setTimeframe] = useState("1d");

    const [sentiment, setSentiment] = useState<string>("");
    const [marketSentimentLabel, setMarketSentimentLabel] = useState<MarketSentimentLabel | null>(null);
    const [loadingSentiment, setLoadingSentiment] = useState(false);

    const priceQuery = useQuery({
        queryKey: queryKeys.tickerPrice(apiSymbol, timeframe),
        queryFn: ({ signal }) =>
            apiRequest<any>("GET", `/api/price-chart?ticker=${apiSymbol}&days=${timeframe}`, { signal }),
        staleTime: 30_000,
    });

    const optionsChainQuery = useQuery({
        queryKey: queryKeys.tickerOptions(apiSymbol),
        queryFn: ({ signal }) =>
            apiRequest<OptionsSnapshotResponse | null>("GET", `/api/options-chain?ticker=${apiSymbol}`, { signal }),
        staleTime: 30_000,
    });

    const newsQuery = useQuery({
        queryKey: ["ticker", apiSymbol, "news-and-quote"] as const,
        queryFn: async ({ signal }) => {
            const [finvizData, quoteData] = await Promise.all([
                apiRequest<any>("GET", `/api/finviz?ticker=${apiSymbol}`, { signal }),
                apiRequest<any>("GET", `/api/quotes?ticker=${apiSymbol}`, { signal }),
            ]);
            return { metadata: finvizData, quote: quoteData?.metrics || null };
        },
        staleTime: 60_000,
    });

    const optionsChain = optionsChainQuery.data ?? null;
    const priceData = priceQuery.data ?? null;
    const loading = optionsChainQuery.isPending || optionsChainQuery.isFetching;
    const priceLoading = priceQuery.isPending || priceQuery.isFetching;
    const metadata = newsQuery.data?.metadata ?? null;
    const quote = newsQuery.data?.quote ?? null;
    const newsLoading = newsQuery.isPending || newsQuery.isFetching;

    const optionsData = useMemo(
        () => optionsChain?.results || [],
        [optionsChain],
    );

    const expirationDates = useMemo(() => {
        if (!optionsData?.length) return [];
        return Array.from(
            new Set(optionsData.map((o) => o.details.expiration_date)),
        ).sort();
    }, [optionsData]);

    useEffect(() => {
        if (expirationDates.length > 0 && !selectedExpiry) {
            setSelectedExpiry(expirationDates[0]);
        }
    }, [expirationDates, selectedExpiry]);

    const underlyingPrice = useMemo(() => {
        return optionsData[0]?.underlying_asset?.price || 0;
    }, [optionsData]);

    const preferUnderlyingPrice = INDEX_LIKE_SYMBOLS.has(symbol);
    const displayedSpotPrice =
        Number(quote?.close) ||
        (preferUnderlyingPrice
            ? Number(underlyingPrice) || Number(priceData?.values?.[0]?.close)
            : Number(priceData?.values?.[0]?.close) || Number(underlyingPrice)) ||
        Number(metadata?.metrics?.Price) ||
        0;

    const filteredOptionsData = useMemo(() => {
        if (!optionsData?.length || !selectedExpiry) return [];
        return optionsData.filter(
            (o) => o.details.expiration_date === selectedExpiry,
        );
    }, [optionsData, selectedExpiry]);

    const strikes = useMemo(() => {
        if (!filteredOptionsData.length) return [];
        return [
            ...new Set(filteredOptionsData.map((o) => o.details.strike_price)),
        ].sort((a, b) => a - b);
    }, [filteredOptionsData]);

    // JS-1082: idea-context chart overlays -- ALL absent (empty arrays /
    // null) when `resolvedIdeaContext` is null, which reproduces the
    // pre-JS-1082 candlestick chart/options-chain panel byte-for-byte (see
    // TickerDashboard.test.tsx's regression pin).
    const ideaEntry = resolvedIdeaContext?.entry ?? null;
    // JS-1094 (demo blocker FIX 4): projected via the SAME priced-legs
    // projection the screener/edition drawers use (screenerLabels.ts) --
    // `ideaEntry.strikes`/`ideaEntry.expiry` read RAW would be honestly
    // empty for a real, priced CREDIT entry (bull-put/bear-call/condor),
    // which never populates those canonical fields (only the debit pipeline
    // does). The banner below used to read those raw fields directly, so a
    // credit idea's context banner silently omitted its own strikes/expiry
    // even though the chart overlays (strikeMarkLines/exitWindowShading,
    // already projected) and every other Desk-idea surface showed them.
    const ideaDisplayStrikes = useMemo(() => (ideaEntry ? projectedDisplayStrikes(ideaEntry) : []), [ideaEntry]);
    const ideaDisplayExpiry = useMemo(() => (ideaEntry ? projectedDisplayExpiry(ideaEntry) : null), [ideaEntry]);
    const ideaPriceLevelMarkLines = useMemo(() => {
        if (!ideaEntry) return [];
        return [...strikeMarkLines(ideaEntry), ...breakevenMarkLines(ideaEntry)];
    }, [ideaEntry]);
    const ideaExitWindow = useMemo(
        () => (ideaEntry ? exitWindowShading(ideaEntry) : null),
        [ideaEntry],
    );
    // Idea legs' own strikes, for OptionsChainPanel's additive highlight
    // prop -- parsed from the SAME `entry.strikes` the price-chart markLines
    // above read, never re-derived from priced_legs separately.
    const ideaHighlightedStrikes = useMemo(
        () => ideaPriceLevelMarkLines.filter((line) => line.label.startsWith("Strike ")).map((line) => line.price),
        [ideaPriceLevelMarkLines],
    );
    // The exit rule's own factual sentence (screenerLabels.exitRuleCopy) --
    // the honest "stop-ref" surface for the banner (see
    // tickerIdeaOverlays.ts's module docstring for why a stop is never
    // plotted as a fabricated underlying-price markLine).
    const ideaExitRuleSentence = ideaEntry ? exitRuleCopy(ideaEntry.exit_rule ?? null) : null;

    // JS-1097 (adversarial-review fix): the "full argument" expandable
    // detail is now a GENUINE composition of the SAME
    // `EntryFourQuestionArgument` (ThesisQuestionSections.tsx) the screener
    // drawer and the edition card both assemble their own four-question
    // body from -- not a parallel, hand-rolled Q1-Q4 reconstruction that can
    // silently diverge (the prior version dropped `entry.provenance_tags`,
    // which the context response DOES carry). Collapsed by default so the
    // chart stays the primary surface; expanding reveals the complete
    // published thesis behind the idea in context.
    //
    // The ticker view's idea context (`IdeaContextResponse`) carries only
    // `entry` -- unlike the edition's `MultiStrategyScreenRow`, it has no
    // `setup`/`setup_binding`, `relative_strength`/`options_flow`/`earnings`
    // sibling facts, or `entries` (alternatives) list, because the backend
    // endpoint (JS-1082) resolves ONE entry by ledger_id, never a whole row.
    // These are EXPLICIT, individually-optional props on the shared
    // component -- passing none of them here is a documented, honest
    // absence (Q1 falls back to `WhyNowFallback`'s "no bound setup thesis"
    // disclosure, no watch-outs, no alternatives), never a silently
    // omitted field this endpoint simply forgot to carry.
    const [argumentExpanded, setArgumentExpanded] = useState(false);
    const ideaWhyCodes = ideaEntry?.reason_codes ?? [];
    const ideaAbstain = ideaEntry?.abstain_reason_codes ?? [];
    const ideaAbstainHeadline =
        ideaAbstain.length > 0 ? reasonCodeLabel(ideaAbstain[0]) : reasonCodeLabel("DEBIT_ECONOMICS_NO_ELIGIBLE_EXPRESSION");

    const chartData = useMemo(() => {
        if (!strikes.length) return [];
        return strikes.map((strike) => {
            const call = filteredOptionsData.find(
                (o) =>
                    o.details.strike_price === strike &&
                    o.details.contract_type === "call",
            );
            const put = filteredOptionsData.find(
                (o) =>
                    o.details.strike_price === strike &&
                    o.details.contract_type === "put",
            );
            return {
                strike,
                callOi: call?.open_interest || 0,
                putOi: put?.open_interest || 0,
                callVol: call?.day?.volume || 0,
                putVol: put?.day?.volume || 0,
                callDelta: call?.greeks?.delta || 0,
                putDelta: put?.greeks?.delta || 0,
                callGamma: call?.greeks?.gamma || 0,
                putGamma: put?.greeks?.gamma || 0,
                callTheta: call?.greeks?.theta || 0,
                putTheta: put?.greeks?.theta || 0,
                callVega: call?.greeks?.vega || 0,
                putVega: put?.greeks?.vega || 0,
                callIv: call?.implied_volatility || 0,
                putIv: put?.implied_volatility || 0,
            };
        });
    }, [strikes, filteredOptionsData]);

    const overallSentiment = useMemo(() => {
        if (marketSentimentLabel) return marketSentimentLabel;
        if (!sentiment) return null;
        const lower = sentiment.toLowerCase();
        if (lower.includes("positive")) return "Positive";
        if (lower.includes("negative")) return "Negative";
        if (lower.includes("neutral")) return "Neutral";
        return null;
    }, [marketSentimentLabel, sentiment]);

    const unusualVolumesData = useMemo(() => {
        if (!filteredOptionsData.length) return [];
        return filteredOptionsData
            .map((o) => {
                const volume = o.day?.volume || 0;
                const oi = o.open_interest || 0;
                const ratio = oi > 0 ? volume / oi : 0;
                return {
                    strike: o.details.strike_price,
                    type: o.details.contract_type.toUpperCase(),
                    expiration: o.details.expiration_date,
                    volume,
                    oi,
                    ratio,
                    iv: o.implied_volatility || 0,
                };
            })
            .filter((d) => d.ratio > 2 || d.volume > 5000)
            .sort((a, b) => b.ratio - a.ratio)
            .slice(0, 15);
    }, [filteredOptionsData]);

    const highestOiData = useMemo(() => {
        if (!chartData.length) return [];
        return [...chartData]
            .sort((a, b) => {
                const valA =
                    optionType === "call"
                        ? a.callOi
                        : optionType === "put"
                            ? a.putOi
                            : a.callOi + a.putOi;
                const valB =
                    optionType === "call"
                        ? b.callOi
                        : optionType === "put"
                            ? b.putOi
                            : b.callOi + b.putOi;
                return valB - valA;
            })
            .slice(0, 10);
    }, [chartData, optionType]);

    const oiByExpiry = useMemo(() => {
        if (!optionsData.length) return [];
        const grouped: Record<string, { call: number; put: number }> = {};
        optionsData.forEach((o) => {
            const date = o.details.expiration_date;
            if (!grouped[date]) grouped[date] = { call: 0, put: 0 };
            if (o.details.contract_type === "call")
                grouped[date].call += o.open_interest || 0;
            else grouped[date].put += o.open_interest || 0;
        });
        return Object.entries(grouped)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, oi]) => ({ date, callOi: oi.call, putOi: oi.put }));
    }, [optionsData]);

    const highestVolData = useMemo(() => {
        if (!chartData.length) return [];
        return [...chartData]
            .sort((a, b) => {
                const valA =
                    optionType === "call"
                        ? a.callVol
                        : optionType === "put"
                            ? a.putVol
                            : a.callVol + a.putVol;
                const valB =
                    optionType === "call"
                        ? b.callVol
                        : optionType === "put"
                            ? b.putVol
                            : b.callVol + b.putVol;
                return valB - valA;
            })
            .slice(0, 10);
    }, [chartData, optionType]);

    const volByExpiry = useMemo(() => {
        if (!optionsData.length) return [];
        const grouped: Record<string, { call: number; put: number }> = {};
        optionsData.forEach((o) => {
            const date = o.details.expiration_date;
            if (!grouped[date]) grouped[date] = { call: 0, put: 0 };
            if (o.details.contract_type === "call")
                grouped[date].call += o.day?.volume || 0;
            else grouped[date].put += o.day?.volume || 0;
        });
        return Object.entries(grouped)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, vol]) => ({ date, callVol: vol.call, putVol: vol.put }));
    }, [optionsData]);

    const calculateRSI = useCallback((data: any[], period = 14) => {
        if (!data || data.length < period + 1) return [];

        const prices = [...data].reverse().map((d) => Number(d.close));
        const changes = [];
        for (let i = 1; i < prices.length; i++) {
            changes.push(prices[i] - prices[i - 1]);
        }

        const rsiValues = [];
        let avgGain =
            changes
                .slice(0, period)
                .filter((c) => c > 0)
                .reduce((a, b) => a + b, 0) / period;
        let avgLoss =
            Math.abs(
                changes
                    .slice(0, period)
                    .filter((c) => c < 0)
                    .reduce((a, b) => a + b, 0),
            ) / period;

        // Corrected calculation to avoid mutation and ensure proper timeframe alignment
        const reversedData = [...data].reverse();
        for (let i = period; i < changes.length; i++) {
            const currentChange = changes[i];
            const gain = currentChange > 0 ? currentChange : 0;
            const loss = currentChange < 0 ? Math.abs(currentChange) : 0;

            avgGain = (avgGain * (period - 1) + gain) / period;
            avgLoss = (avgLoss * (period - 1) + loss) / period;

            const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
            const rsi = 100 - 100 / (1 + rs);
            rsiValues.push({
                datetime: reversedData[i + 1]?.datetime,
                rsi: rsi,
            });
        }
        return rsiValues;
    }, []);

    const rsiData = useMemo(() => {
        if (!priceData?.values) return [];
        return calculateRSI(priceData.values);
    }, [priceData, calculateRSI]);

    const currentRSI = useMemo(() => {
        return rsiData[rsiData.length - 1]?.rsi || 62.5;
    }, [rsiData]);

    const metrics = useMemo(() => {
        if (!optionsChain?.results)
            return { pcr: 0, vol: 0, oi: 0, iv: 0, clusters: [] };
        const results = optionsChain.results;
        const callOi = results
            .filter((c) => c.details.contract_type === "call")
            .reduce((acc, c) => acc + (c.open_interest || 0), 0);
        const putOi = results
            .filter((c) => c.details.contract_type === "put")
            .reduce((acc, c) => acc + (c.open_interest || 0), 0);
        const totalVol = results.reduce((acc, c) => acc + (c.day?.volume || 0), 0);
        const totalOi = callOi + putOi;
        const avgIv =
            results.reduce((acc, c) => acc + (c.implied_volatility || 0), 0) /
            (results.length || 1);

        // Find top 3 OI clusters (strikes with highest OI)
        const strikeOiMap = new Map<number, number>();
        results.forEach((c) => {
            const strike = c.details.strike_price;
            strikeOiMap.set(
                strike,
                (strikeOiMap.get(strike) || 0) + (c.open_interest || 0),
            );
        });
        const topClusters = Array.from(strikeOiMap.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(
                ([strike]) =>
                    `${strike}${results.find((c) => c.details.strike_price === strike)?.details.contract_type === "call" ? "c" : "p"}`,
            );

        return {
            pcr: callOi > 0 ? putOi / callOi : 0,
            vol: totalVol,
            oi: totalOi,
            iv: avgIv,
            clusters: topClusters,
        };
    }, [optionsChain]);

    const fetchSentimentalAnalysisApi = useCallback(async (ticker: string) => {
        try {
            setLoadingSentiment(true);
            setSentiment("");
            setMarketSentimentLabel(null);

            const response = await apiRequest<MarketSentimentResponse>("POST", "/lh-agents-apis/v1/tools/market_sentiment", {
                body: {
                    ticker,
                    include_sources: true,
                },
            });
            setMarketSentimentLabel(response.sentiment);
            setSentiment(response.summary);
        } catch {
            setSentiment("Market sentiment is temporarily unavailable. Please try again later.");
            setMarketSentimentLabel(null);
        } finally {
            setLoadingSentiment(false);
        }
    }, []);

    useEffect(() => {
        fetchSentimentalAnalysisApi(apiSymbol.trim());
    }, [apiSymbol, fetchSentimentalAnalysisApi]);

    const renderMetadata = ({
        activeTab = "overview",
        tabControl,
    }: {
        activeTab?: "overview" | "chain";
        tabControl?: ReactNode;
    } = {}) => {
        const price = displayedSpotPrice || 0;
        const change = quote?.change || "0.00";
        const changePercent = quote?.percent_change || "0.00";
        const isPositive = Number(change) >= 0;

        return (
            <div
                className={activeTab === "overview" ? "min-h-full flex flex-col" : ""}
                style={{ borderBottom: "1px solid var(--v2-border)", background: "var(--v2-bg-surface)" }}
            >
                <div className="p-4 pb-3">
                    <div className="flex flex-col gap-0.5 mb-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xl font-bold leading-none" style={{ color: "var(--v2-text-primary)" }}>
                                {symbol}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded leading-none" style={{ color: "var(--v2-text-muted)", background: "var(--v2-bg-card)" }}>
                                {metadata?.metrics?.Industry || "Stock"}
                            </span>
                        </div>
                        <div className="text-[11px] font-medium truncate" style={{ color: "var(--v2-text-muted)" }}>
                            {metadata?.metrics?.Company || symbol}
                        </div>
                    </div>

                    <div className="flex flex-col mb-4">
                        <div className="text-2xl font-bold leading-none mb-1" style={{ color: "var(--v2-text-primary)" }}>
                            ${" "}
                            {Number(price).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </div>
                        <div
                            className={`text-xs font-medium flex items-center gap-1 ${isPositive ? "text-[#0ECB81]" : "text-[#F6465D]"}`}
                        >
                            <span>
                                {isPositive ? "+" : ""}
                                {Number(change).toFixed(2)}
                            </span>
                            <span>
                                ({isPositive ? "+" : ""}
                                {Number(changePercent).toFixed(2)}%)
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[10px]">
                        <div className="flex flex-col">
                            <span className="uppercase font-bold tracking-tight" style={{ color: "var(--v2-text-muted)" }}>
                                Market Cap
                            </span>
                            <span className="font-mono" style={{ color: "var(--v2-text-primary)" }}>
                                {metadata?.metrics?.["Market Cap"] || "-"}
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <span className="uppercase font-bold tracking-tight" style={{ color: "var(--v2-text-muted)" }}>
                                52W Range
                            </span>
                            <span className="font-mono text-[9px]" style={{ color: "var(--v2-text-primary)" }}>
                                {quote?.fifty_two_week_range || "-"}
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <span className="uppercase font-bold tracking-tight" style={{ color: "var(--v2-text-muted)" }}>
                                Expected Move
                            </span>
                            <span className="font-mono" style={{ color: "var(--v2-text-primary)" }}>-</span>
                        </div>
                    </div>
                </div>

                {tabControl ? (
                    <div>
                        {tabControl}
                    </div>
                ) : null}

                {activeTab === "overview" ? (
                    <div className="flex-1 p-4" style={{ borderTop: "1px solid var(--v2-border)" }}>
                    {/* Header */}
                    <div className="text-[10px] font-bold uppercase tracking-wider mb-3 flex justify-between" style={{ color: "var(--v2-text-primary)" }}>
                        <span>Options Overview</span>
                        <span style={{ color: "var(--v2-text-muted)" }}>#</span>
                    </div>

                    <div className="flex flex-col gap-3">

                        {/* Put / Call Ratio */}
                        <div className="rounded-md p-3" style={{ background: "var(--v2-bg-card)", border: "1px solid var(--v2-border)" }}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-[10px]" style={{ color: "var(--v2-text-muted)" }}>Put/Call Ratio</span>
                                <span className="text-[11px] font-mono font-bold" style={{ color: "var(--v2-text-primary)" }}>
                                    {metrics.pcr.toFixed(2)}
                                </span>
                            </div>

                            {/* Ratio Bar */}
                            <div className="w-full h-1.5 rounded overflow-hidden flex" style={{ background: "var(--v2-border)" }}>
                                {metrics.pcr > 0 ? (
                                    <>
                                        <div
                                            style={{ width: `${Math.min(metrics.pcr, 1) * 100}%`, background: "#ef4444" }}
                                        />
                                        <div
                                            style={{ width: `${100 - Math.min(metrics.pcr, 1) * 100}%`, background: "#22c55e" }}
                                        />
                                    </>
                                ) : (
                                    <div style={{ width: "100%", background: "var(--v2-border)" }} />
                                )}
                            </div>

                            <div className="flex justify-between text-[9px] mt-1" style={{ color: "var(--v2-text-muted)" }}>
                                <span>Put Volume</span>
                                <span>Call Volume</span>
                            </div>
                        </div>

                        {/* Relative Volume */}
                        <div className="rounded-md p-3" style={{ background: "var(--v2-bg-card)", border: "1px solid var(--v2-border)" }}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-[10px]" style={{ color: "var(--v2-text-muted)" }}>Relative Volume</span>
                                <span className="text-[11px] font-mono font-bold" style={{ color: "var(--v2-text-primary)" }}>
                                    {metadata?.metrics?.["Rel Volume"] || "0.00"}x
                                </span>
                            </div>

                            {/* Relative Volume Bar */}
                            {(() => {
                                const relVol = Number(metadata?.metrics?.["Rel Volume"] || 0)
                                const percent = Math.min(relVol / 2, 1) * 100 // scale up to 2x
                                return (
                                    <>
                                        <div className="w-full h-1.5 rounded overflow-hidden" style={{ background: "var(--v2-border)" }}>
                                            <div
                                                className="h-full"
                                                style={{ width: `${percent}%`, background: "#3b82f6" }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-[9px] mt-1" style={{ color: "var(--v2-text-muted)" }}>
                                            <span>Low</span>
                                            <span>Avg (1.0x)</span>
                                            <span>High</span>
                                        </div>
                                    </>
                                )
                            })()}
                        </div>

                        {/* Top OI Clusters */}
                        <div>
                            <span className="text-[10px]" style={{ color: "var(--v2-text-muted)" }}>Top OI Clusters</span>
                            <div className="flex gap-2 mt-1 flex-wrap">
                                {metrics.clusters.length > 0 ? (
                                    metrics.clusters.map((c) => (
                                        <span
                                            key={c}
                                            className="text-[10px] px-2 py-0.5 rounded font-mono font-bold"
                                            style={{ background: "rgba(17,115,212,0.10)", color: "#1173d4", border: "1px solid rgba(17,115,212,0.2)" }}
                                        >
                                            {c}
                                        </span>
                                    ))
                                ) : (
                                    <span className="font-mono" style={{ color: "var(--v2-text-dim)" }}>-</span>
                                )}
                            </div>
                        </div>

                        {/* Bottom Stats */}
                        <div className="grid grid-cols-3 pt-2" style={{ borderTop: "1px solid var(--v2-border)" }}>
                            <div className="flex flex-col items-start">
                                <span className="text-[13px] font-bold tabular-nums" style={{ color: "var(--v2-text-primary)" }}>
                                    {metrics.vol.toLocaleString()}
                                </span>
                                <span className="text-[9px] uppercase" style={{ color: "var(--v2-text-muted)" }}>
                                    Total Volume
                                </span>
                            </div>

                            <div className="flex flex-col items-center">
                                <span className="text-[13px] font-bold tabular-nums" style={{ color: "var(--v2-text-primary)" }}>
                                    {metrics.oi.toLocaleString()}
                                </span>
                                <span className="text-[9px] uppercase" style={{ color: "var(--v2-text-muted)" }}>
                                    Open Interest
                                </span>
                            </div>

                            <div className="flex flex-col items-end">
                                <span className="text-[13px] font-bold tabular-nums" style={{ color: "var(--v2-text-primary)" }}>
                                    {(metrics.iv * 100).toFixed(2)}%
                                </span>
                                <span className="text-[9px] uppercase" style={{ color: "var(--v2-text-muted)" }}>
                                    Avg IV
                                </span>
                            </div>
                        </div>

                    </div>
                    </div>
                ) : null}
            </div>
        );
    };

    const bottomTabs = [
        { id: "options-chain", label: "Options", icon: List },
        { id: "ai-insight", label: "AI Insight", icon: Sparkles },
        { id: "open-interest", label: "Open Interest", icon: BarChart2 },
        { id: "volume", label: "Volume", icon: BarChart2 },
        { id: "greeks", label: "Greeks", icon: Calculator },
        { id: "volatility-skew", label: "Volatility Skew", icon: Activity },
        { id: "unusual-volumes", label: "Unusual Volumes", icon: Zap },
    ];
    const isMobileTallChartTab =
        activeBottomTab === "greeks" || activeBottomTab === "volatility-skew";

    const DashboardSkeleton = () => (
        <div className="flex-1 flex flex-col" style={{ background: "var(--v2-bg-panel)" }}>
            <SkeletonStyles />
            {/* Toolbar shimmer */}
            <div className="h-12 flex items-center px-4 gap-4" style={{ borderBottom: "1px solid var(--v2-border)" }}>
                <SkeletonText width={120} height={6} />
                <SkeletonText width="60%" height={4} style={{ opacity: 0.5 }} />
            </div>
            <div className="flex-1 grid grid-cols-12 overflow-hidden">
                {/* Main chart area */}
                <div className="col-span-9 flex flex-col" style={{ borderRight: "1px solid var(--v2-border)" }}>
                    <div className="h-10 flex items-center px-4 gap-2" style={{ borderBottom: "1px solid var(--v2-border)" }}>
                        {[1, 2, 3, 4].map((i) => (
                            <SkeletonText key={i} width={56} height={5} />
                        ))}
                    </div>
                    <div className="flex-1 p-4" style={{ background: "var(--v2-bg-surface)" }}>
                        <SkeletonChart height="100%" style={{ borderRadius: 8 }} />
                    </div>
                    <div className="h-32 p-4" style={{ borderTop: "1px solid var(--v2-border)", background: "var(--v2-bg-surface)" }}>
                        <SkeletonChart height="100%" style={{ borderRadius: 6 }} />
                    </div>
                </div>
                {/* Sidebar */}
                <div className="col-span-3 flex flex-col p-4 gap-4" style={{ background: "var(--v2-bg-panel)" }}>
                    <SkeletonText width={80} height={6} />
                    <SkeletonText width={100} height={22} />
                    <SkeletonText width="70%" height={4} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                                <SkeletonText width="40%" height={10} />
                                <SkeletonText width="25%" height={10} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="h-36 p-4 flex gap-4" style={{ borderTop: "1px solid var(--v2-border)", background: "var(--v2-bg-panel)" }}>
                <SkeletonChart height="100%" style={{ flex: 1, opacity: 0.5 }} />
                <SkeletonChart height="100%" style={{ flex: 1, opacity: 0.5 }} />
            </div>
        </div>
    );

    return (
        <>
            {/* Scoped styles for v2 dashboard */}
            <style>{`
        @import url("https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&display=swap");

        .v2-dashboard ::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        .v2-dashboard ::-webkit-scrollbar-track {
          background: var(--v2-scrollbar-track);
        }
        .v2-dashboard ::-webkit-scrollbar-thumb {
          background: var(--v2-scrollbar-thumb);
          border-radius: 2px;
        }
        .v2-dashboard ::-webkit-scrollbar-thumb:hover {
          background: var(--v2-scrollbar-hover);
        }
        .v2-dashboard .tabular-nums {
          font-feature-settings: "tnum";
          font-variant-numeric: tabular-nums;
        }
        .v2-dashboard .v2-chart-grid {
          background-size: 40px 40px;
          background-image:
            linear-gradient(to right, var(--v2-grid-line) 1px, transparent 1px),
            linear-gradient(to bottom, var(--v2-grid-line) 1px, transparent 1px);
          opacity: 0.25;
        }
        @keyframes v2ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .v2-dashboard .animate-v2ticker {
          animation: v2ticker 30s linear infinite;
        }
        .v2-dashboard .mask-linear-gradient {
          mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
        }
        .v2-dashboard .font-mono {
          font-family: "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
        }
        .v2-dashboard select option {
          background: var(--v2-bg-panel);
          color: var(--v2-text-primary);
        }
      `}</style>

            <div className="v2-dashboard font-sans overflow-hidden flex flex-col" style={{ height: "100dvh", background: "var(--v2-bg-surface)", color: "var(--v2-text-primary)", transition: "background 0.2s, color 0.2s" }}>
                <SkeletonStyles />
                <AppHeader />
                {loading ? (
                    <DashboardSkeleton />
                ) : (
                    <main className="flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-12 lg:grid-rows-[minmax(0,1fr)_120px_240px] overflow-y-auto lg:overflow-hidden" style={{ background: "var(--v2-bg-surface)" }}>
                        {/* ─── Candlestick Chart Area ─── */}
                        <section className="col-span-12 lg:col-span-9 lg:row-span-1 min-h-[450px] lg:min-h-0 flex flex-col relative border-b lg:border-b-0 lg:border-r" style={{ background: "var(--v2-bg-base)", borderColor: "var(--v2-border)" }}>
                            {/* Chart Toolbar */}
                            <div className="h-9 border-b flex items-center justify-between px-3" style={{ background: "var(--v2-bg-panel)", borderColor: "var(--v2-border)" }}>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-1 text-xs font-medium" style={{ color: "var(--v2-text-muted)" }}>
                                        {(["1d", "5d", "1m", "6m", "ytd", "1y", "5Y"] as const).map(
                                            (tf) => (
                                                <span
                                                    key={tf}
                                                    onClick={() => setTimeframe(tf)}
                                                    className={`${timeframe === tf ? "font-bold" : "hover:bg-(--v2-bg-hover)"} px-1.5 py-0.5 rounded cursor-pointer transition-colors uppercase`}
                                                    style={{
                                                        color: timeframe === tf ? "var(--v2-text-primary)" : "var(--v2-text-muted)",
                                                        background: timeframe === tf ? "var(--v2-bg-surface)" : "transparent",
                                                        border: timeframe === tf ? "1px solid var(--v2-border)" : "1px solid transparent"
                                                    }}
                                                >
                                                    {tf}
                                                </span>
                                            ),
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* JS-1082: idea-context banner -- present ONLY when the
                                deep link's ledger_id resolved to a real, still-stored
                                idea. Absent entirely (no extra DOM, no layout shift)
                                for the context-free case, which is the overwhelming
                                majority of visits to this page (regression-pinned in
                                TickerDashboard.test.tsx). Copy is factual only, per
                                ADR-011 -- it states what the idea IS, never advice. */}
                            {resolvedIdeaContext && ideaEntry && (
                                <div
                                    className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-1.5 text-[11px]"
                                    style={{ background: "var(--v2-bg-panel)", borderColor: "var(--v2-border)", color: "var(--v2-text-muted)" }}
                                >
                                    <span style={{ color: "var(--v2-text-primary)" }}>
                                        Viewing in context of Desk idea: {resolvedIdeaContext.ticker ?? symbol}{" "}
                                        {/* JS-1087 (adversarial-review fix, defense-in-depth): a direction
                                            outside BULLISH/BEARISH used to map to an empty string here --
                                            an invisible state (a dangling double space, no honest signal)
                                            for any idea context that reaches this banner with an
                                            unrecognized direction (e.g. a legacy/legacy-linked context
                                            that slipped past the upstream disabled-link gates in
                                            MultiStrategyScreenTable.tsx / EditionPage.tsx). Renders the
                                            SAME "direction unavailable" fact those two surfaces use
                                            (screenerLabels.directionView), styled neutrally rather than
                                            silently vanishing. */}
                                        {directionView(resolvedIdeaContext.direction).kind === "BULLISH" ? (
                                            "Long"
                                        ) : directionView(resolvedIdeaContext.direction).kind === "BEARISH" ? (
                                            "Short"
                                        ) : (
                                            <span style={{ color: "var(--v2-text-muted)" }}>
                                                {directionView(resolvedIdeaContext.direction).label}
                                            </span>
                                        )}{" "}
                                        {archetypeLabel(ideaEntry.archetype)}
                                        {/* JS-1094 (demo blocker FIX 4): projected strikes/expiry (see
                                            `ideaDisplayStrikes`/`ideaDisplayExpiry` above), not the raw
                                            canonical fields -- a priced credit entry's canonical
                                            strikes/expiry are legitimately empty. */}
                                        {ideaDisplayStrikes.length > 0 ? ` ${ideaDisplayStrikes.join(" / ")}` : ""}
                                        {ideaDisplayExpiry ? ` · ${ideaDisplayExpiry}` : ""}
                                    </span>
                                    {/* JS-1082 round 2 (FIX 1): the TIER-2 (durable/archived)
                                        case is called out honestly -- this idea's live
                                        screener row is gone, so what's shown below is its
                                        permanent publication-time record, not a current
                                        market reading. */}
                                    {resolvedIdeaContext.context_source === "OUTCOME_LEDGER" && (
                                        <span className="italic" style={{ color: "var(--v2-text-dim)" }}>
                                            archived idea — publication-time facts only
                                        </span>
                                    )}
                                    {ideaExitRuleSentence && <span>{ideaExitRuleSentence}</span>}
                                    <span className="ml-auto flex items-center gap-3">
                                        {/* JS-1097: the full four-question argument behind this
                                            idea, collapsed by default so the banner stays a
                                            one-line strip -- same "Show full argument" toggle
                                            convention as the edition card's expandable detail
                                            (EditionPage.tsx's IdeaCard). */}
                                        <button
                                            type="button"
                                            aria-expanded={argumentExpanded}
                                            onClick={() => setArgumentExpanded((value) => !value)}
                                            className="flex items-center gap-1 font-medium hover:underline"
                                            style={{ color: "var(--v2-text-primary)" }}
                                        >
                                            {argumentExpanded ? "Hide full argument" : "Show full argument"}
                                            <ChevronDown
                                                className={cn("size-3 transition-transform", argumentExpanded && "rotate-180")}
                                                aria-hidden
                                            />
                                        </button>
                                        {backToIdeaHref && (
                                            <Link
                                                to={backToIdeaHref}
                                                className="font-medium underline-offset-2 hover:underline"
                                                style={{ color: "#1173d4" }}
                                            >
                                                ← Back to idea
                                            </Link>
                                        )}
                                    </span>
                                </div>
                            )}

                            {/* JS-1097 (adversarial-review fix): the expandable "full
                                argument" detail -- a GENUINE composition of the SAME
                                EntryFourQuestionArgument (ThesisQuestionSections.tsx) the
                                screener drawer and the daily edition's card assemble their
                                own four-question body from (NO-TECH-DEBT reuse), never a
                                parallel hand-rolled reconstruction. Row-scoped props
                                (setup/watchOutCodes/alternatives) are left unset -- an
                                explicit, documented absence (see the comment above
                                `ideaWhyCodes`), since this endpoint resolves one entry, not
                                a whole row. */}
                            {resolvedIdeaContext && ideaEntry && argumentExpanded && (
                                <div
                                    className="max-h-[45vh] overflow-y-auto border-b px-3 py-3"
                                    style={{ background: "var(--v2-bg-panel)", borderColor: "var(--v2-border)" }}
                                >
                                    <div className="space-y-4 rounded-md border px-3 py-3" style={{ borderColor: "var(--v2-border)", background: "var(--v2-bg-surface)" }}>
                                        <EntryFourQuestionArgument
                                            entry={ideaEntry}
                                            reasonCodes={ideaWhyCodes}
                                            archetypeLabel={archetypeLabel}
                                            abstainHeadline={ideaAbstainHeadline}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Price Overlay */}
                            <div className="absolute top-11 left-4 z-10 flex flex-col pointer-events-none">
                                <div className="flex items-baseline gap-3">
                                    <h1 className="text-xl font-bold tracking-wide" style={{ color: "var(--v2-text-primary)" }}>
                                        {symbol}
                                    </h1>
                                    <span className="text-base font-mono text-[#0ECB81] tabular-nums font-semibold">
                                        {displayedSpotPrice ? displayedSpotPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}
                                    </span>
                                    <span
                                        className={`text-xs font-medium px-1.5 py-0.5 rounded ${Number(priceData?.values?.[0]?.close) > Number(priceData?.values?.[1]?.close) ? "text-[#0ECB81] bg-[#0ECB81]/10" : "text-[#F6465D] bg-[#F6465D]/10"}`}
                                    >
                                        {
                                            quote?.percent_change
                                                ? `${quote?.change > 0 ? "+" : ""}${Number(quote?.percent_change).toFixed(2)}%`
                                                : "+5.80%"
                                        }
                                    </span>
                                </div>
                                <div className="flex gap-4 text-[10px] font-mono mt-1" style={{ color: "var(--v2-text-muted)" }}>
                                    <span className="flex gap-1">
                                        <span>Vol:</span>{" "}
                                        <span style={{ color: "var(--v2-text-primary)" }}>
                                            {priceData?.values?.[0]?.volume || "-"}
                                        </span>
                                    </span>
                                    <span className="flex gap-1">
                                        <span>H:</span>{" "}
                                        <span style={{ color: "var(--v2-text-primary)" }}>
                                            {priceData?.values?.[0]?.high || "-"}
                                        </span>
                                    </span>
                                    <span className="flex gap-1">
                                        <span>L:</span>{" "}
                                        <span style={{ color: "var(--v2-text-primary)" }}>
                                            {priceData?.values?.[0]?.low || "-"}
                                        </span>
                                    </span>
                                </div>
                            </div>

                            {/* ECharts Candlestick Chart */}
                            <div className="flex-1 relative w-full h-full overflow-hidden" style={{ background: "var(--v2-bg-surface)" }}>
                                <div className="absolute inset-0 v2-chart-grid pointer-events-none"></div>
                                {priceLoading ? (
                                    <div className="absolute inset-0 z-20" style={{ padding: 16 }}>
                                        <SkeletonChart height="100%" style={{ borderRadius: 8 }} />
                                    </div>
                                ) : null}
                                <ReactECharts
                                    style={{ height: "100%", width: "100%" }}
                                    option={{
                                        backgroundColor: "transparent",
                                        tooltip: {
                                            trigger: "axis",
                                            axisPointer: { type: "cross" },
                                            backgroundColor: chartColors.tooltipBg,
                                            borderColor: chartColors.tooltipBorder,
                                            textStyle: { color: chartColors.tooltipText },
                                        },
                                        grid: { left: 50, right: 30, top: 20, bottom: 55 },
                                        xAxis: {
                                            type: "category",
                                            data: (priceData?.values || [])
                                                .map((v: any) => v.datetime)
                                                .reverse(),
                                            axisLine: { lineStyle: { color: chartColors.gridLine } },
                                            axisLabel: { color: chartColors.axisLabel, fontSize: 10 },
                                        },
                                        yAxis: {
                                            scale: true,
                                            axisLine: { show: false },
                                            axisLabel: { color: chartColors.axisLabel, fontSize: 10 },
                                            splitLine: {
                                                lineStyle: { color: chartColors.gridLine, type: "dashed" },
                                            },
                                        },
                                        dataZoom: [
                                            { type: "inside", start: 0, end: 100 },
                                            {
                                                type: "slider",
                                                bottom: 10,
                                                height: 35,
                                                start: 0,
                                                end: 100,
                                                borderColor: "transparent",
                                                fillerColor: "rgba(17, 115, 212, 0.1)",
                                                handleStyle: { color: "#1173d4" },
                                                textStyle: { color: "var(--v2-text-muted)", fontSize: 9 },
                                            },
                                        ],
                                        series: [
                                            {
                                                type: "candlestick",
                                                data: (priceData?.values || [])
                                                    .map((v: any) => [
                                                        Number(v.open),
                                                        Number(v.close),
                                                        Number(v.low),
                                                        Number(v.high),
                                                    ])
                                                    .reverse(),
                                                itemStyle: {
                                                    color: "#0ECB81",
                                                    color0: "#F6465D",
                                                    borderColor: "#0ECB81",
                                                    borderColor0: "#F6465D",
                                                },
                                                // JS-1082 round 2 (FIX 5): `markLine`/`markArea` are
                                                // OMITTED FROM THE OPTION OBJECT ENTIRELY (never present
                                                // as an empty-data placeholder) when there is no idea in
                                                // context -- byte-identical to the pre-JS-1082 chart,
                                                // which never declared either key at all. An
                                                // empty-but-present `markLine: { data: [] }` would still
                                                // be a NEW key on the option object even though it
                                                // renders nothing -- this pin is about the option's own
                                                // shape, not merely its visual output.
                                                ...(ideaPriceLevelMarkLines.length > 0
                                                    ? {
                                                          markLine: {
                                                              symbol: "none",
                                                              data: priceLevelsToEchartsMarkLineData(ideaPriceLevelMarkLines),
                                                              label: { show: false },
                                                          },
                                                      }
                                                    : {}),
                                                ...(ideaExitWindow
                                                    ? {
                                                          markArea: {
                                                              data: exitWindowToEchartsMarkAreaData(ideaExitWindow),
                                                          },
                                                      }
                                                    : {}),
                                            },
                                        ],
                                    }}
                                />
                            </div>
                        </section>

                        {/* ─── Right Sidebar: Options Chain + News (Desktop Only) ─── */}
                        <aside className="hidden lg:flex lg:col-span-3 lg:row-span-3 h-full min-h-0 flex-col border-l min-w-[280px] overflow-hidden" style={{ background: "var(--v2-bg-panel)", borderColor: "var(--v2-border)" }}>
                            <ErrorBoundary section="ticker.sidebar-options">
                                <Suspense fallback={<TabFallback />}>
                                    <OptionsChainPanel
                                        strikes={strikes}
                                        filteredOptionsData={filteredOptionsData}
                                        underlyingPrice={underlyingPrice}
                                        metadata={metadata}
                                        metrics={metrics}
                                        highlightedStrikes={ideaHighlightedStrikes}
                                        overviewContent={newsLoading ? (
                                            <div className="p-4" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <SkeletonText width={60} height={18} />
                                                    <SkeletonText width={50} height={14} />
                                                </div>
                                                <SkeletonText width={140} height={10} />
                                                <SkeletonText width={100} height={24} />
                                                <SkeletonText width={80} height={12} />
                                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                                                    {[1, 2, 3].map((i) => (
                                                        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                            <SkeletonText width="70%" height={8} />
                                                            <SkeletonText width="50%" height={10} />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                                                    {[1, 2, 3, 4, 5].map((i) => (
                                                        <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                                                            <SkeletonText width="45%" height={10} />
                                                            <SkeletonText width="30%" height={10} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : ({ activeTab, tabControl }) => renderMetadata({ activeTab, tabControl })}
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        </aside>

                        {/* RSI Indicator Panel */}
                        <section className="col-span-12 lg:col-span-9 lg:row-span-1 min-h-[150px] lg:min-h-0 relative flex flex-col" style={{ background: "var(--v2-bg-surface)", borderTop: "1px solid var(--v2-border)", borderRight: "1px solid var(--v2-border)" }}>
                            <div className="absolute top-2 left-2 z-10 flex items-center gap-2">
                                <span className="text-[10px] font-bold text-[#B96DF5] uppercase tracking-wider">
                                    RSI (14)
                                </span>
                                <span
                                    className={`text-[10px] font-mono ${currentRSI > 70 ? "text-[#F6465D]" : currentRSI < 30 ? "text-[#0ECB81]" : ""}`}
                                    style={{ color: currentRSI > 70 ? undefined : currentRSI < 30 ? undefined : "var(--v2-text-primary)" }}
                                >
                                    {currentRSI.toFixed(1)}
                                </span>
                            </div>
                            <div className="flex-1 relative w-full h-full overflow-hidden" style={{ background: "var(--v2-bg-surface)" }}>
                                <div className="absolute inset-0 v2-chart-grid pointer-events-none"></div>
                                <ReactECharts
                                    style={{ height: "100%", width: "100%" }}
                                    option={{
                                        backgroundColor: "transparent",
                                        tooltip: {
                                            trigger: "axis",
                                            backgroundColor: chartColors.tooltipBg,
                                            borderColor: chartColors.tooltipBorder,
                                            textStyle: { color: chartColors.tooltipText },
                                        },
                                        grid: { top: 20, left: 50, right: 30, bottom: 20 },
                                        xAxis: {
                                            type: "category",
                                            data: rsiData.map((d: any) => d.datetime),
                                            show: false,
                                        },
                                        yAxis: {
                                            min: 0,
                                            max: 100,
                                            interval: 20,
                                            axisLine: { show: false },
                                            axisLabel: { color: chartColors.axisLabel, fontSize: 9 },
                                            splitLine: {
                                                lineStyle: {
                                                    color: chartColors.gridLine,
                                                    type: "dashed",
                                                },
                                            },
                                        },
                                        series: [
                                            {
                                                type: "line",
                                                data: rsiData.map((d: any) => d.rsi),
                                                smooth: true,
                                                showSymbol: false,
                                                lineStyle: { color: "#B96DF5", width: 1.5 },
                                                areaStyle: {
                                                    color: {
                                                        type: "linear",
                                                        x: 0,
                                                        y: 0,
                                                        x2: 0,
                                                        y2: 1,
                                                        colorStops: [
                                                            { offset: 0, color: "rgba(185, 109, 245, 0.2)" },
                                                            { offset: 1, color: "transparent" },
                                                        ],
                                                    },
                                                },
                                                markLine: {
                                                    symbol: "none",
                                                    data: [
                                                        {
                                                            yAxis: 70,
                                                            lineStyle: {
                                                                color: "rgba(246, 70, 93, 0.4)",
                                                                type: "solid",
                                                            },
                                                        },
                                                        {
                                                            yAxis: 30,
                                                            lineStyle: {
                                                                color: "rgba(14, 203, 129, 0.4)",
                                                                type: "solid",
                                                            },
                                                        },
                                                    ],
                                                    label: { show: false },
                                                },
                                            },
                                        ],
                                        dataZoom: [{ type: "inside" }],
                                    }}
                                />
                            </div>
                        </section>

                        {/* ─── Options Analysis / Bottom Panel ─── */}
                        <section className="col-span-12 lg:col-span-9 lg:row-span-1 min-h-fit lg:min-h-0 flex flex-col" style={{ background: "var(--v2-bg-panel)", borderTop: "1px solid var(--v2-border)", borderRight: "1px solid var(--v2-border)" }}>
                            {/* Tab Bar Container */}
                            <div className="ticker-bottom-header flex flex-col lg:flex-row lg:items-center justify-between min-h-[36px]" style={{ borderBottom: "1px solid var(--v2-border)", background: "var(--v2-bg-panel)" }}>
                                {/* Tabs Row */}
                                <div className="ticker-bottom-tabs flex h-9 lg:h-full overflow-x-auto no-scrollbar" style={{ borderBottom: "1px solid var(--v2-border)" }}>
                                    {bottomTabs.map((tab) => {
                                        const Icon = tab.icon;
                                        const isActive = activeBottomTab === tab.id;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveBottomTab(tab.id)}
                                                className={`px-4 text-[10px] lg:text-xs font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap ${tab.id === "options-chain" ? "lg:hidden" : ""}`}
                                                style={{
                                                    color: isActive ? "#1173d4" : "var(--v2-text-muted)",
                                                    borderBottom: isActive ? "2px solid #1173d4" : "2px solid transparent",
                                                    background: isActive ? "var(--v2-bg-hover)" : "transparent",
                                                }}
                                            >
                                                <Icon size={14} />
                                                {tab.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Option Type + Expiry Controls Row */}
                                <div className="ticker-dashboard-controls flex items-center justify-between lg:justify-end gap-3 px-3 py-1.5 lg:py-0">
                                    <div className="ticker-dashboard-control-group flex items-center gap-2">
                                        {activeBottomTab === "greeks" && (
                                            <div className="flex items-center rounded overflow-hidden mr-2" style={{ background: "var(--v2-bg-surface)", border: "1px solid var(--v2-border)" }}>
                                                {(["delta", "gamma", "theta", "vega"] as const).map(
                                                    (greek) => (
                                                        <button
                                                            key={greek}
                                                            onClick={() => setActiveGreek(greek)}
                                                            className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors ${activeGreek === greek
                                                                ? "bg-[#1173d4]/20 text-[#1173d4]"
                                                                : "text-(--v2-text-muted) hover:text-(--v2-text-primary) hover:bg-(--v2-bg-hover)"
                                                                }`}
                                                        >
                                                            {greek}
                                                        </button>
                                                    ),
                                                )}
                                            </div>
                                        )}

                                        {/* Call / Put / Both segmented control */}
                                        <div
                                            className={`flex items-center rounded overflow-hidden ${activeBottomTab === "options-chain" || activeBottomTab === "ai-insight" ? "hidden lg:flex" : "flex"}`}
                                            style={{ background: "var(--v2-bg-surface)", border: "1px solid var(--v2-border)" }}
                                        >
                                            {(["call", "put", "both"] as const).map((type) => (
                                                <button
                                                    key={type}
                                                    onClick={() => setOptionType(type)}
                                                    className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors"
                                                    style={{
                                                        color: optionType === type
                                                            ? type === "call" ? "#0ECB81" : type === "put" ? "#F6465D" : "#1173d4"
                                                            : "var(--v2-text-muted)",
                                                        background: optionType === type
                                                            ? type === "call" ? "rgba(14,203,129,0.12)" : type === "put" ? "rgba(246,70,93,0.12)" : "rgba(17,115,212,0.12)"
                                                            : "transparent",
                                                    }}
                                                >
                                                    {type}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Expiry dropdown */}
                                        <div
                                            className={`ticker-expiry-control flex items-center gap-1.5 ml-auto lg:ml-0 ${activeBottomTab === "ai-insight"
                                            ? "hidden lg:flex"
                                            : "flex"
                                            }`}
                                    >
                                        <span className="text-[10px] font-medium" style={{ color: "var(--v2-text-muted)" }}>
                                            Exp:
                                        </span>
                                        <select
                                            value={selectedExpiry}
                                            onChange={(e) => setSelectedExpiry(e.target.value)}
                                            className="ticker-expiry-select text-[10px] rounded px-2 py-1 focus:outline-none cursor-pointer font-mono"
                                            style={{
                                                background: "var(--v2-bg-panel)",
                                                color: "var(--v2-text-primary)",
                                                border: "1px solid var(--v2-border)",
                                            }}
                                        >
                                            {expirationDates.map((date) => (
                                                <option key={date} value={date}>
                                                    {date}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={`flex-1 overflow-hidden flex flex-col relative ${activeBottomTab === "options-chain" ? "p-0 lg:p-3" : "p-3"} ${isMobileTallChartTab ? "min-h-[560px] lg:min-h-0" : ""}`}
                                style={{ background: "var(--v2-bg-surface)" }}
                            >
                                {activeBottomTab === "options-chain" ? (
                                    <ErrorBoundary section="ticker.options-chain">
                                        <Suspense fallback={<TabFallback />}>
                                            <OptionsChainPanel
                                                strikes={strikes}
                                                filteredOptionsData={filteredOptionsData}
                                                underlyingPrice={underlyingPrice}
                                                metadata={metadata}
                                                metrics={metrics}
                                                highlightedStrikes={ideaHighlightedStrikes}
                                            />
                                        </Suspense>
                                    </ErrorBoundary>
                                ) : activeBottomTab === "ai-insight" ? (
                                    <ErrorBoundary section="ticker.ai-insight">
                                        <Suspense fallback={<TabFallback />}>
                                            <AIInsightPanel
                                                sentiment={sentiment}
                                                loadingSentiment={loadingSentiment}
                                                overallSentiment={overallSentiment}
                                            />
                                        </Suspense>
                                    </ErrorBoundary>
                                ) : activeBottomTab === "unusual-volumes" ? (
                                    <div className="flex-1 overflow-auto">
                                        <table className="w-full text-[11px] text-left">
                                            <thead className="uppercase font-bold sticky top-0 z-10" style={{ color: "var(--v2-text-dim)", background: "var(--v2-bg-surface)" }}>
                                                <tr>
                                                    <th className="px-2 py-1.5">Strike</th>
                                                    <th className="px-2 py-1.5">Type</th>
                                                    <th className="px-2 py-1.5">Exp</th>
                                                    <th className="px-2 py-1.5 text-right">Vol</th>
                                                    <th className="px-2 py-1.5 text-right">OI</th>
                                                    <th className="px-2 py-1.5 text-right">Ratio</th>
                                                    <th className="px-2 py-1.5 text-right">IV</th>
                                                </tr>
                                            </thead>
                                            <tbody style={{ borderTop: "1px solid var(--v2-border)" }}>
                                                {unusualVolumesData.length > 0 ? (
                                                    unusualVolumesData.map((row, idx) => (
                                                        <tr
                                                            key={idx}
                                                            className="transition-colors"
                                                            style={{
                                                                borderBottom: "1px solid var(--v2-border-soft)",
                                                                background: row.type === "CALL" ? "var(--v2-bg-success-subtle)" : "var(--v2-bg-danger-subtle)"
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = "var(--v2-bg-hover)"}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = row.type === "CALL" ? "var(--v2-bg-success-subtle)" : "var(--v2-bg-danger-subtle)"}
                                                        >
                                                            <td className="px-2 py-1.5 font-bold tabular-nums" style={{ color: "var(--v2-text-primary)" }}>
                                                                {row.strike}
                                                            </td>
                                                            <td
                                                                className="px-2 py-1.5 font-bold"
                                                                style={{ color: row.type === "CALL" ? "#0ECB81" : "#F6465D" }}
                                                            >
                                                                {row.type}
                                                            </td>
                                                            <td className="ticker-table-expiry px-2 py-1.5" style={{ color: "var(--v2-text-muted)" }}>
                                                                {row.expiration}
                                                            </td>
                                                            <td className="px-2 py-1.5 text-right font-mono" style={{ color: "var(--v2-text-primary)" }}>
                                                                {row.volume.toLocaleString()}
                                                            </td>
                                                            <td className="px-2 py-1.5 text-right font-mono" style={{ color: "var(--v2-text-dim)" }}>
                                                                {row.oi.toLocaleString()}
                                                            </td>
                                                            <td className="px-2 py-1.5 text-right font-bold font-mono" style={{ color: row.ratio > 1.5 ? "var(--v2-text-primary)" : "var(--v2-text-muted)" }}>
                                                                {row.ratio.toFixed(2)}x
                                                            </td>
                                                            <td className="px-2 py-1.5 text-right font-mono" style={{ color: "var(--v2-text-muted)" }}>
                                                                {(row.iv * 100).toFixed(1)}%
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td
                                                            colSpan={7}
                                                            className="px-2 py-4 text-center"
                                                            style={{ color: "var(--v2-text-dim)" }}
                                                        >
                                                            No unusual volumes detected
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : activeBottomTab === "open-interest" ||
                                    activeBottomTab === "volume" ? (
                                    chartData.length === 0 ? (
                                        <div className="flex-1 flex items-center justify-center">
                                            <span className="text-sm italic" style={{ color: "var(--v2-text-dim)" }}>No data available</span>
                                        </div>
                                    ) : (
                                        <div className="flex-1 flex flex-col lg:flex-row gap-4 overflow-x-auto lg:overflow-x-visible overflow-y-visible lg:overflow-y-hidden pb-1">
                                            {/* Main Chart: By Strike */}
                                            <div className="flex-1 min-h-[450px] lg:min-h-0 min-w-[300px] lg:min-w-[400px]">
                                                <div className="text-[10px] font-bold text-(--v2-text-dim) uppercase mb-1 px-2">
                                                    {activeBottomTab === "open-interest"
                                                        ? "OI by Strike"
                                                        : "Volume by Strike"}
                                                </div>
                                                <ReactECharts
                                                    notMerge={true}
                                                    style={{ height: "calc(100% - 20px)", width: "100%" }}
                                                    option={{
                                                        backgroundColor: "transparent",
                                                        tooltip: {
                                                            trigger: "axis",
                                                            backgroundColor: chartColors.tooltipBg,
                                                            borderColor: chartColors.tooltipBorder,
                                                            textStyle: { color: chartColors.tooltipText },
                                                        },
                                                        grid: {
                                                            top: 20,
                                                            left: 50,
                                                            right: 20,
                                                            bottom: 45,
                                                            containLabel: true,
                                                        },
                                                        xAxis: {
                                                            type: "category",
                                                            data: strikes,
                                                            axisLine: { lineStyle: { color: chartColors.gridLine } },
                                                            axisLabel: { color: chartColors.axisLabel, fontSize: 9 },
                                                        },
                                                        yAxis: {
                                                            type: "value",
                                                            axisLine: { show: false },
                                                            axisLabel: { color: chartColors.axisLabel, fontSize: 9 },
                                                            splitLine: {
                                                                lineStyle: { color: chartColors.gridLine, type: "dashed" },
                                                            },
                                                        },
                                                        series:
                                                            activeBottomTab === "open-interest"
                                                                ? [
                                                                    (optionType === "both" ||
                                                                        optionType === "call") && {
                                                                        name: "Call OI",
                                                                        type: "bar",
                                                                        data: chartData.map((d) => d.callOi),
                                                                        itemStyle: { color: "#0ECB81" },
                                                                    },
                                                                    (optionType === "both" ||
                                                                        optionType === "put") && {
                                                                        name: "Put OI",
                                                                        type: "bar",
                                                                        data: chartData.map((d) => d.putOi),
                                                                        itemStyle: { color: "#F6465D" },
                                                                    },
                                                                ].filter(Boolean)
                                                                : [
                                                                    (optionType === "both" ||
                                                                        optionType === "call") && {
                                                                        name: "Call Vol",
                                                                        type: "bar",
                                                                        data: chartData.map((d) => d.callVol),
                                                                        itemStyle: { color: "#0ECB81" },
                                                                    },
                                                                    (optionType === "both" ||
                                                                        optionType === "put") && {
                                                                        name: "Put Vol",
                                                                        type: "bar",
                                                                        data: chartData.map((d) => d.putVol),
                                                                        itemStyle: { color: "#F6465D" },
                                                                    },
                                                                ].filter(Boolean),
                                                        dataZoom: [
                                                            { type: "inside" },
                                                            { type: "slider", height: 30, bottom: 5 },
                                                        ],
                                                    }}
                                                />
                                            </div>
                                            {/* Secondary Chart: Top 10 */}
                                            <div className="w-full lg:w-[300px] min-h-[450px] lg:min-h-0">
                                                <div className="text-[10px] font-bold text-(--v2-text-dim) uppercase mb-1 px-2">
                                                    Top 10 Strikes
                                                </div>
                                                <ReactECharts
                                                    notMerge={true}
                                                    style={{ height: "calc(100% - 20px)", width: "100%" }}
                                                    option={{
                                                        backgroundColor: "transparent",
                                                        tooltip: {
                                                            trigger: "axis",
                                                            backgroundColor: chartColors.tooltipBg,
                                                            borderColor: chartColors.tooltipBorder,
                                                            textStyle: { color: chartColors.tooltipText },
                                                        },
                                                        grid: {
                                                            top: 20,
                                                            left: 50,
                                                            right: 10,
                                                            bottom: 30,
                                                            containLabel: true,
                                                        },
                                                        xAxis: {
                                                            type: "category",
                                                            data: (activeBottomTab === "open-interest"
                                                                ? highestOiData
                                                                : highestVolData
                                                            ).map((d) => d.strike),
                                                            axisLabel: { color: chartColors.axisLabel, fontSize: 9 },
                                                        },
                                                        yAxis: {
                                                            type: "value",
                                                            axisLabel: { show: false },
                                                            splitLine: {
                                                                lineStyle: { color: chartColors.gridLine, type: "dashed" },
                                                            },
                                                        },
                                                        series:
                                                            activeBottomTab === "open-interest"
                                                                ? [
                                                                    {
                                                                        name:
                                                                            optionType === "both"
                                                                                ? "Total OI"
                                                                                : optionType === "call"
                                                                                    ? "Call OI"
                                                                                    : "Put OI",
                                                                        type: "bar",
                                                                        data: highestOiData.map((d) =>
                                                                            optionType === "call"
                                                                                ? d.callOi
                                                                                : optionType === "put"
                                                                                    ? d.putOi
                                                                                    : d.callOi + d.putOi,
                                                                        ),
                                                                        itemStyle: {
                                                                            color:
                                                                                optionType === "call"
                                                                                    ? "#0ECB81"
                                                                                    : optionType === "put"
                                                                                        ? "#F6465D"
                                                                                        : "#1173d4",
                                                                        },
                                                                        emphasis: { focus: "series" },
                                                                    },
                                                                ]
                                                                : [
                                                                    {
                                                                        name:
                                                                            optionType === "both"
                                                                                ? "Total Vol"
                                                                                : optionType === "call"
                                                                                    ? "Call Vol"
                                                                                    : "Put Vol",
                                                                        type: "bar",
                                                                        data: highestVolData.map((d) =>
                                                                            optionType === "call"
                                                                                ? d.callVol
                                                                                : optionType === "put"
                                                                                    ? d.putVol
                                                                                    : d.callVol + d.putVol,
                                                                        ),
                                                                        itemStyle: {
                                                                            color:
                                                                                optionType === "call"
                                                                                    ? "#0ECB81"
                                                                                    : optionType === "put"
                                                                                        ? "#F6465D"
                                                                                        : "#1173d4",
                                                                        },
                                                                        emphasis: { focus: "series" },
                                                                    },
                                                                ],
                                                        dataZoom: [{ type: "inside" }],
                                                    }}
                                                />
                                            </div>
                                            {/* Tertiary Chart: By Expiry */}
                                            <div className="w-full lg:w-[300px] min-h-[450px] lg:min-h-0">
                                                <div className="text-[10px] font-bold text-(--v2-text-dim) uppercase mb-1 px-2">
                                                    Concentration by Expiry
                                                </div>
                                                <ReactECharts
                                                    notMerge={true}
                                                    style={{ height: "calc(100% - 20px)", width: "100%" }}
                                                    option={{
                                                        backgroundColor: "transparent",
                                                        tooltip: {
                                                            trigger: "axis",
                                                            backgroundColor: chartColors.tooltipBg,
                                                            borderColor: chartColors.tooltipBorder,
                                                            textStyle: { color: chartColors.tooltipText },
                                                        },
                                                        grid: {
                                                            top: 20,
                                                            left: 50,
                                                            right: 10,
                                                            bottom: 30,
                                                            containLabel: true,
                                                        },
                                                        xAxis: {
                                                            type: "category",
                                                            data: (activeBottomTab === "open-interest"
                                                                ? oiByExpiry
                                                                : volByExpiry
                                                            ).map((d) => d.date),
                                                            axisLabel: {
                                                                color: chartColors.axisLabel,
                                                                fontSize: 8,
                                                                rotate: 45,
                                                            },
                                                        },
                                                        yAxis: {
                                                            type: "value",
                                                            axisLabel: { show: false },
                                                            splitLine: {
                                                                lineStyle: { color: chartColors.gridLine, type: "dashed" },
                                                            },
                                                        },
                                                        series:
                                                            activeBottomTab === "open-interest"
                                                                ? [
                                                                    (optionType === "both" ||
                                                                        optionType === "call") && {
                                                                        name: "Call OI",
                                                                        type: "bar",
                                                                        stack: "total",
                                                                        data: oiByExpiry.map((d) => d.callOi),
                                                                        itemStyle: { color: "#0ECB81" },
                                                                        emphasis: { focus: "series" },
                                                                    },
                                                                    (optionType === "both" ||
                                                                        optionType === "put") && {
                                                                        name: "Put OI",
                                                                        type: "bar",
                                                                        stack: "total",
                                                                        data: oiByExpiry.map((d) => d.putOi),
                                                                        itemStyle: { color: "#F6465D" },
                                                                        emphasis: { focus: "series" },
                                                                    },
                                                                ].filter(Boolean)
                                                                : [
                                                                    (optionType === "both" ||
                                                                        optionType === "call") && {
                                                                        name: "Call Vol",
                                                                        type: "bar",
                                                                        stack: "total",
                                                                        data: volByExpiry.map((d) => d.callVol),
                                                                        itemStyle: { color: "#0ECB81" },
                                                                        emphasis: { focus: "series" },
                                                                    },
                                                                    (optionType === "both" ||
                                                                        optionType === "put") && {
                                                                        name: "Put Vol",
                                                                        type: "bar",
                                                                        stack: "total",
                                                                        data: volByExpiry.map((d) => d.putVol),
                                                                        itemStyle: { color: "#F6465D" },
                                                                        emphasis: { focus: "series" },
                                                                    },
                                                                ].filter(Boolean),
                                                        dataZoom: [{ type: "inside" }],
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    chartData.length === 0 ? (
                                        <div className="flex-1 flex items-center justify-center">
                                            <span className="text-(--v2-text-dim) text-sm italic">No data available</span>
                                        </div>
                                    ) : (
                                        <div className="flex-1 min-h-[520px] lg:min-h-0">
                                            <ReactECharts
                                                notMerge={true}
                                                style={{ height: "100%", width: "100%" }}
                                                option={{
                                                backgroundColor: "transparent",
                                                tooltip: {
                                                    trigger: "axis",
                                                    backgroundColor: chartColors.tooltipBg,
                                                    borderColor: chartColors.tooltipBorder,
                                                    textStyle: { color: chartColors.tooltipText },
                                                    axisPointer: {
                                                        type: "shadow",
                                                        lineStyle: { color: chartColors.gridLine },
                                                    },
                                                },
                                                grid: {
                                                    top: 30,
                                                    left: 50,
                                                    right: 30,
                                                    bottom: 55,
                                                    containLabel: true,
                                                },
                                                xAxis: {
                                                    type:
                                                        activeBottomTab === "volatility-skew" ||
                                                            activeBottomTab === "greeks"
                                                            ? "value"
                                                            : "category",
                                                    data:
                                                        activeBottomTab === "volatility-skew" ||
                                                            activeBottomTab === "greeks"
                                                            ? undefined
                                                            : strikes,
                                                    min:
                                                        activeBottomTab === "volatility-skew" ||
                                                            activeBottomTab === "greeks"
                                                            ? "dataMin"
                                                            : undefined,
                                                    max:
                                                        activeBottomTab === "volatility-skew" ||
                                                            activeBottomTab === "greeks"
                                                            ? "dataMax"
                                                            : undefined,
                                                    axisLine: { lineStyle: { color: chartColors.gridLine } },
                                                    axisLabel: { color: chartColors.axisLabel, fontSize: 10 },
                                                    splitLine: { show: false },
                                                },
                                                yAxis: {
                                                    type: "value",
                                                    axisLine: { show: false },
                                                    axisLabel: { color: chartColors.axisLabel, fontSize: 10 },
                                                    splitLine: {
                                                        lineStyle: { color: chartColors.gridLine, type: "dashed" },
                                                    },
                                                },
                                                dataZoom: [
                                                    { type: "inside" },
                                                    { type: "slider", height: 30, bottom: 5 },
                                                ],
                                                series: (() => {
                                                    if (activeBottomTab === "greeks") {
                                                        const key =
                                                            activeGreek === "delta"
                                                                ? "Delta"
                                                                : activeGreek === "gamma"
                                                                    ? "Gamma"
                                                                    : activeGreek === "theta"
                                                                        ? "Theta"
                                                                        : "Vega";
                                                        return [
                                                            (optionType === "both" ||
                                                                optionType === "call") && {
                                                                name: `Call ${key}`,
                                                                type: "line",
                                                                smooth: true,
                                                                data: chartData.map((d) => [
                                                                    d.strike,
                                                                    d[`call${key}` as keyof typeof d],
                                                                ]),
                                                                itemStyle: { color: "#0ECB81" },
                                                            },
                                                            (optionType === "both" || optionType === "put") && {
                                                                name: `Put ${key}`,
                                                                type: "line",
                                                                smooth: true,
                                                                data: chartData.map((d) => [
                                                                    d.strike,
                                                                    d[`put${key}` as keyof typeof d],
                                                                ]),
                                                                itemStyle: { color: "#F6465D" },
                                                            },
                                                        ].filter(Boolean);
                                                    }
                                                    if (activeBottomTab === "volatility-skew") {
                                                        return [
                                                            (optionType === "both" ||
                                                                optionType === "call") && {
                                                                name: "Call IV",
                                                                type: "line",
                                                                smooth: true,
                                                                data: chartData.map((d) => [d.strike, d.callIv]),
                                                                itemStyle: { color: "#0ECB81" },
                                                            },
                                                            (optionType === "both" || optionType === "put") && {
                                                                name: "Put IV",
                                                                type: "line",
                                                                smooth: true,
                                                                data: chartData.map((d) => [d.strike, d.putIv]),
                                                                itemStyle: { color: "#F6465D" },
                                                            },
                                                        ].filter(Boolean);
                                                    }
                                                    return [];
                                                })(),
                                                }}
                                            />
                                        </div>
                                    )
                                )}
                            </div>
                        </section>
                    </main>
                )}
            </div>
        </>
    );
}
